/**
 * DateRangeFilter Component
 *
 * Date range filter with presets and custom date selection.
 */

import { useState, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { Button } from '../../../../ui/button';
import { Input } from '../../../../ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../ui/popover';
import type { DateRangeFilterProps, DateRangePreset } from '../types/filter.types';
import { DATE_RANGE_PRESETS, formatDateRangeLabel } from '../constants/filter.constants';

export function DateRangeFilter({
  value,
  onChange,
  disabled = false,
  className,
}: DateRangeFilterProps) {
  const [open, setOpen] = useState(false);

  // Local state for custom date inputs — only propagated when both are set
  const [customFrom, setCustomFrom] = useState(value?.from || '');
  const [customTo, setCustomTo] = useState(value?.to || '');

  // Sync local state when value prop is cleared externally (e.g., reset or clear)
  useEffect(() => {
    setCustomFrom(value?.from || '');
    setCustomTo(value?.to || '');
  }, [value?.from, value?.to]);

  // Track whether we're in custom mode (selected but possibly incomplete)
  const isCustomMode = value?.preset === 'custom' || (!value?.preset && (customFrom || customTo));

  // Custom range only counts as having a value when both dates are set
  const isCustomWithBothDates = value?.preset === 'custom' && value?.from && value?.to;
  const isPresetOtherThanCustom = value?.preset && value.preset !== 'custom';
  const hasValue = isPresetOtherThanCustom || isCustomWithBothDates;

  // Check if custom range has incomplete dates (only one date entered)
  const hasIncompleteDateRange = isCustomMode && ((customFrom && !customTo) || (!customFrom && customTo));

  // Check if custom range has dates in the wrong order
  const hasInvalidDateRange = isCustomMode && customFrom && customTo && customFrom > customTo;

  // Get display label
  const getDisplayLabel = () => {
    if (!hasValue) {
      return 'Date Range';
    }
    return formatDateRangeLabel(value?.preset, value?.from, value?.to);
  };

  // Handle preset selection
  const handlePresetSelect = (preset: DateRangePreset) => {
    if (preset === 'custom') {
      // Enter custom mode without activating the filter
      setCustomFrom(value?.from || '');
      setCustomTo(value?.to || '');
      onChange({ preset: 'custom' });
    } else {
      // Clear local custom state and activate preset
      setCustomFrom('');
      setCustomTo('');
      onChange({ preset });
      setOpen(false);
    }
  };

  // Handle custom date changes — only propagate when both dates are filled and from <= to
  const handleFromChange = (from: string) => {
    setCustomFrom(from);
    if (from && customTo && from <= customTo) {
      onChange({ preset: 'custom', from, to: customTo });
    }
  };

  const handleToChange = (to: string) => {
    setCustomTo(to);
    if (customFrom && to && customFrom <= to) {
      onChange({ preset: 'custom', from: customFrom, to });
    }
  };

  // Clear the filter
  const handleClear = () => {
    setCustomFrom('');
    setCustomTo('');
    onChange(undefined);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'justify-between font-normal',
            hasValue && 'text-foreground',
            !hasValue && 'text-muted-foreground',
            className
          )}
        >
          <Calendar className="mr-2 h-4 w-4" />
          <span className="truncate">{getDisplayLabel()}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <div className="p-2">
          {/* Presets */}
          <div className="space-y-1">
            {DATE_RANGE_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => handlePresetSelect(preset.value)}
                className={cn(
                  'w-full rounded-md px-3 py-2 text-left text-sm hover:bg-accent',
                  value?.preset === preset.value && 'bg-accent'
                )}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Custom date inputs */}
          {isCustomMode && (
            <div className="mt-3 space-y-3 border-t pt-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  From
                </label>
                <Input
                  type="date"
                  value={customFrom}
                  onChange={(e) => handleFromChange(e.target.value)}
                  className="h-8"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  To
                </label>
                <Input
                  type="date"
                  value={customTo}
                  onChange={(e) => handleToChange(e.target.value)}
                  className="h-8"
                />
              </div>
              {hasIncompleteDateRange && (
                <p className="text-xs text-destructive">
                  Please select both start and end dates
                </p>
              )}
              {hasInvalidDateRange && (
                <p className="text-xs text-destructive">
                  End date cannot be before start date
                </p>
              )}
            </div>
          )}

          {/* Clear button */}
          {hasValue && (
            <div className="mt-3 border-t pt-3">
              <button
                type="button"
                onClick={handleClear}
                className="w-full rounded-md px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent"
              >
                Clear date filter
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
